// components/workspace-solo-modal.js — SOLO WORKSPACE CREATION MODAL
// ═══════════════════════════════════════════════════════════
// Same structural look as components/invite-modal.js but simpler:
//   - No space type selection (always Solo)
//   - No email field (no invitee)
//   - No consent checkbox
// Just three orientation fields:
//   1. Space Name (required)
//   2. Your role (free text — what hat are you wearing for this work?)
//   3. What would you like to achieve (required — session intent)
//
// Submit creates a new shared_spaces row with:
//   is_workspace        = true
//   space_type          = 'space' (neutral — Solo is a MODE not a type)
//   created_by          = current user
//   name                = Space Name
//   workspace_metadata  = { solo: true, role, goal }
// And adds the creator as the sole participant. Then the caller opens
// the session view immediately.
//
// Props:
//   onClose(): close without creating
//   onCreate({ name, role, goal }): submit handler (async). Should
//     create the workspace row, add the participant, and open the session.
//   creating: boolean — disables submit while in flight
// ═══════════════════════════════════════════════════════════
import { useState } from 'react';

export default function WorkspaceSoloModal({ onClose, onCreate, creating }) {
  const [name, setName] = useState('');
  const [role, setRole] = useState('');
  const [customRole, setCustomRole] = useState('');
  const [goal, setGoal] = useState('');
  const [error, setError] = useState('');

  const ROLE_CHIPS = [
    'Researcher', 'Analyst', 'Founder', 'Writer',
    'Trader', 'Investor', 'Consultant', 'Teacher',
    'Student', 'Advisor', 'Designer', 'Other…',
  ];

  async function handleCreate() {
    if (!name.trim())  { setError('Space name is required.'); return; }
    if (!goal.trim())  { setError('Tell Shift what you want to achieve.'); return; }
    setError('');
    const effectiveRole = role === 'Other…' ? (customRole.trim() || 'Other') : role;
    try {
      await onCreate({ name: name.trim(), role: effectiveRole, goal: goal.trim() });
    } catch (e) {
      setError(e?.message || 'Failed to create workspace.');
    }
  }

  const inputStyle = {
    width: '100%',
    background: '#111',
    border: '1px solid #272736',
    borderRadius: 6,
    color: '#d4d4d4',
    fontFamily: 'Georgia, serif',
    fontSize: 12,
    padding: '8px 10px',
    outline: 'none',
    boxSizing: 'border-box',
  };
  const labelStyle = {
    color: '#888',
    fontSize: 12,
    marginBottom: 6,
    fontFamily: 'Georgia, serif',
    display: 'block',
  };

  return (
    <div
      className="shift-modal-overlay"
      style={{
        zIndex: 2000,
        background: 'rgba(0,0,0,0.7)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 20,
        fontFamily: 'Georgia, serif',
      }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        style={{
          background: '#07070a',
          border: '1px solid #272736',
          borderRadius: 12,
          width: 460,
          maxHeight: '85vh',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: '14px 20px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: '0.5px solid #1e1e2a',
            flexShrink: 0,
          }}
        >
          <span style={{ color: '#888', fontSize: 14, fontWeight: 'bold' }}>
            New Solo Workspace
          </span>
          <div
            onClick={onClose}
            style={{
              cursor: 'pointer',
              color: '#333',
              fontSize: 18,
              lineHeight: 1,
              padding: '0 4px',
            }}
            onMouseEnter={e => (e.currentTarget.style.color = '#888')}
            onMouseLeave={e => (e.currentTarget.style.color = '#333')}
          >
            ×
          </div>
        </div>

        {/* Body */}
        <div
          style={{
            flex: 1,
            overflowY: 'auto',
            padding: '16px 20px',
            display: 'flex',
            flexDirection: 'column',
            gap: 16,
          }}
        >
          {/* 1. Space name */}
          <div>
            <div style={labelStyle}>
              Space name <span style={{ color: '#c46050' }}>*</span>
            </div>
            <input
              value={name}
              onChange={e => { setName(e.target.value); if (error) setError(''); }}
              placeholder="e.g. Q2 Market Research, Derivatives Deep-Dive"
              style={inputStyle}
              autoFocus
            />
          </div>

          {/* 2. Your role */}
          <div>
            <div style={labelStyle}>Your role</div>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr 1fr',
                gap: 5,
              }}
            >
              {ROLE_CHIPS.map(r => (
                <div
                  key={r}
                  onClick={() => setRole(r)}
                  style={{
                    background: role === r ? 'rgba(122,158,130,0.04)' : '#111',
                    border: `1px solid ${role === r ? '#7a9e8255' : '#272736'}`,
                    borderRadius: 6,
                    padding: '6px 4px',
                    cursor: 'pointer',
                    color: role === r ? '#7a9e82' : '#666',
                    fontSize: 10,
                    textAlign: 'center',
                    transition: 'all 0.12s',
                  }}
                >
                  {r}
                </div>
              ))}
            </div>
            {role === 'Other…' && (
              <input
                value={customRole}
                onChange={e => setCustomRole(e.target.value)}
                placeholder="e.g. Day trader, CPA, Grant writer…"
                style={{ ...inputStyle, fontSize: 11, marginTop: 6 }}
              />
            )}
          </div>

          {/* 3. Goal */}
          <div>
            <div style={labelStyle}>
              What would you like to achieve?{' '}
              <span style={{ color: '#c46050' }}>*</span>
            </div>
            <textarea
              value={goal}
              onChange={e => { setGoal(e.target.value); if (error) setError(''); }}
              rows={3}
              placeholder="Be specific — the more precise the goal, the better Shift can help you get there."
              style={{ ...inputStyle, resize: 'none', lineHeight: 1.6 }}
            />
          </div>

          {/* Error */}
          {error && (
            <div style={{ color: '#c46050', fontSize: 11, fontStyle: 'italic' }}>
              {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div
          style={{
            padding: '12px 20px',
            borderTop: '0.5px solid #1e1e2a',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            flexShrink: 0,
          }}
        >
          <button
            onClick={handleCreate}
            disabled={creating}
            style={{
              background: 'none',
              border: '1px solid #7a9e8233',
              borderRadius: 6,
              color: '#7a9e82',
              fontSize: 11,
              fontFamily: 'Georgia, serif',
              padding: '6px 16px',
              cursor: creating ? 'default' : 'pointer',
              opacity: creating ? 0.5 : 1,
            }}
            onMouseEnter={e => {
              if (!creating)
                e.currentTarget.style.background = 'rgba(122,158,130,0.06)';
            }}
            onMouseLeave={e => (e.currentTarget.style.background = 'none')}
          >
            {creating ? 'Creating…' : 'Create Solo Workspace →'}
          </button>
        </div>
      </div>
    </div>
  );
}
